/**
 * SKTICK-DB-001 Surface B — reading SK HQ Console's evidence payload.
 *
 * Kept free of react-native so the defensive reading is testable on its own.
 * The panel in components/HqEvidencePanel.tsx renders what this returns.
 *
 * `customFields` is UNVALIDATED BY DESIGN — HQ owns the shape and can change
 * it without a migration here. So every field is read defensively: a missing
 * or malformed key yields null and omits its row, and never blanks the panel
 * or throws.
 *
 * `hqExposure` is a DISPLAY STRING, not money. It is never summed, never
 * totalled across rows and never charted — no money column exists outside SK
 * Billing, and parsing this into a number would quietly create one.
 */

/** HQ severity wording. Deliberately NOT the ticket priority label — they differ. */
const SEVERITY_LABEL: Record<string, string> = {
  FIX_TODAY: 'Fix today',
  THIS_WEEK: 'This week',
  WATCH: 'Keep watching',
  // HQ has also been observed sending numeric severities.
  '1': 'Fix today',
  '2': 'This week',
  '3': 'Keep watching',
};

/** Read a custom field as a trimmed string, or null. Never throws on bad shapes. */
function str(cf: Record<string, unknown>, key: string): string | null {
  const v = cf[key];
  if (typeof v === 'string') {
    return v.trim() || null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return String(v);
  }
  return null;
}

/** Read a custom field as a string list. HQ may send an array or a CSV string. */
function list(cf: Record<string, unknown>, key: string): string[] {
  const v = cf[key];
  if (Array.isArray(v)) {
    return v.filter(
      (x): x is string => typeof x === 'string' && x.trim().length > 0,
    );
  }
  if (typeof v === 'string') {
    return v
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Turn a rule code into something readable without pretending to know it. */
export function humanRule(code: string): string {
  return code.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export interface HqEvidence {
  findingId: string | null;
  ruleCode: string | null;
  ruleVersion: string | null;
  severityLabel: string | null;
  exposure: string | null;
  exposureCurrency: string | null;
  corroboration: string[];
  precedentCount: string | null;
  period: string | null;
  detectedAt: string | null;
  targetDeepLink: string | null;
}

export function readHqEvidence(
  customFields: Record<string, unknown> | undefined,
): HqEvidence {
  const cf = customFields ?? {};
  const rawSeverity = str(cf, 'hqSeverity');
  return {
    findingId: str(cf, 'hqFindingId'),
    ruleCode: str(cf, 'hqRuleCode'),
    ruleVersion: str(cf, 'hqRuleVersion'),
    severityLabel: rawSeverity
      ? SEVERITY_LABEL[rawSeverity] ?? rawSeverity
      : null,
    exposure: str(cf, 'hqExposure'),
    exposureCurrency: str(cf, 'hqExposureCurrency'),
    corroboration: list(cf, 'hqCorroboration'),
    precedentCount: str(cf, 'hqPrecedentCount'),
    period: str(cf, 'hqPeriod'),
    detectedAt: str(cf, 'hqDetectedAt'),
    targetDeepLink: str(cf, 'hqTargetDeepLink'),
  };
}

/** Only http(s) links are ever opened — customFields is unvalidated input. */
export function safeUrl(raw: string | null): string | null {
  if (!raw) {
    return null;
  }
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' || u.protocol === 'http:' ? raw : null;
  } catch {
    return null;
  }
}
